import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { capitalize } from 'utils/capitalize';

const GTM_IDS = {
  'demo': 'GTM-T2PPDN9J',
  'circbo': 'GTM-52W54C2V',
};

const GA_TRACKING_IDS = {
  'unexpect3d': 'G-HXQRJS7C1Y',
  'demo': 'G-F37YS05CNR',
};

const METAPIXEL_IDS = {
  'unexpect3d': '1182531743341985',
  'demo': '1198366548412323',
};

const initializeGTM = (gtmId) => {
  // Add Google Tag Manager
  const gtmScript = document.createElement('script');
  gtmScript.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;

  const noscript = document.createElement('noscript');
  noscript.innerHTML = `
    <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
    height="0" width="0" style="display:none;visibility:hidden"></iframe>
  `;

  document.head.appendChild(gtmScript);
  document.body.insertBefore(noscript, document.body.firstChild);
};

const initializeGA = (gaTrackingId) => {
  // Add Google Analytics only if GTM is not enabled to avoid datalayer and event duplication
  const gaScript = document.createElement('script');
  gaScript.async = true;
  gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`;
  
  const gaConfigScript = document.createElement('script');
  gaConfigScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${gaTrackingId}');
  `;

  document.body.appendChild(gaScript);
  document.body.appendChild(gaConfigScript);
}

const initializeMetaPixel = (metapixelId) => {
  // Add Meta Pixel
  const metaScript = document.createElement('script');
  metaScript.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${metapixelId}');
    fbq('track', 'PageView');
  `;

  const noScript = document.createElement('noscript');
  const pixelImg = document.createElement('img');
  pixelImg.height = '1';
  pixelImg.width = '1';
  pixelImg.style.display = 'none';
  pixelImg.src = `https://www.facebook.com/tr?id=${metapixelId}&ev=PageView&noscript=1`;
  noScript.appendChild(pixelImg);

  document.body.appendChild(metaScript);
  document.body.appendChild(noScript);
};

const setupTrackingFunction = (gtmId, gaTrackingId, metapixelId) => {
  window.trackEvent = (eventType, gaEventData = {}, fbEventData = {}) => {
    // Send to Google Tag Manager
    if (window.dataLayer && gtmId) {
      window.dataLayer.push({
        event: eventType,
        ...gaEventData,
        event_timestamp: new Date().toISOString()
      });
    } else {
      // Send to Google Analytics
      if (window.gtag && gaTrackingId) {
        window.gtag('event', eventType, gaEventData);
      }
      // Send to MetaPixel
      if (window.fbq && metapixelId) {
        window.fbq('track', capitalize(eventType), fbEventData);
      }
    }
  }
};

const cleanupTrackingScripts = () => {
  const cleanupSelectors = {
    // Selectors that work with standard querySelectorAll
    standard: [
      'script[src*="googletagmanager.com"]',
      'script[src*="facebook.net"]',
      'iframe[src*="googletagmanager.com"]',
      'img[src*="facebook.com/tr"]'
    ],
    // Elements that need content inspection
    contentFilters: [
      { selector: 'script', content: ['gtag', 'fbq', 'GTM-'] },
      { selector: 'noscript', content: ['facebook.com/tr', 'googletagmanager.com/ns.html'] }
    ]
  };

  // Remove elements matching standard selectors
  cleanupSelectors.standard.forEach(selector => {
    document.querySelectorAll(selector).forEach(el => el.remove());
  });

  // Remove elements that contain specific content
  cleanupSelectors.contentFilters.forEach(({ selector, content }) => {
    document.querySelectorAll(selector).forEach(el => {
      if (content.some(text => el.innerHTML.includes(text))) {
        el.remove();
      }
    });
  });

  if (window.dataLayer) window.dataLayer = undefined;
  if (window.gtag) window.gtag = undefined;
  if (window._fbq) window._fbq = undefined;
  if (window.fbq) window.fbq = undefined;
  if (window.trackEvent) window.trackEvent = undefined;
};

const TrackingScripts = ({ organization }) => {
  if (!organization?.slug) return null;
  
  const gtmId = GTM_IDS[organization.slug];
  const gaTrackingId = GA_TRACKING_IDS[organization.slug];
  const metapixelId = METAPIXEL_IDS[organization.slug];

  if (!gtmId && !gaTrackingId && !metapixelId) return null;

  useEffect(() => {
    
    if (gtmId) {
      initializeGTM(gtmId);
    } else {
      if (gaTrackingId) initializeGA(gaTrackingId);
      if (metapixelId) initializeMetaPixel(metapixelId);
    } 

    setupTrackingFunction(gtmId, gaTrackingId, metapixelId);

    return () => {
      cleanupTrackingScripts();
    };
  }, [gaTrackingId, metapixelId, gtmId]);

  return null;
};

TrackingScripts.propTypes = {
  organization: PropTypes.shape({
    slug: PropTypes.string.isRequired,
  }).isRequired,
};

export default TrackingScripts; 